import { FC, useEffect, useState } from 'react'
import Modal from '@common/Modal'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import classes from './index.module.css'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import { InputNew } from '@UI/InputNew'
import cn from 'classnames'
import { useAddAlert } from '@/hooks/useAddAlert'
import { directionAPI, directionGroupApi, usersAPI } from '@/api'
import { Form, Select } from 'antd'
import Preloader from '@UI/Preloader'
import { FormProps, useForm } from 'antd/es/form/Form'
import FormItem from 'antd/es/form/FormItem'
import { useAsync, useAsyncFn, useEffectOnce } from 'react-use'

type DirectionSett =
  | {
      directionId: number
      enableCourseSelect?: boolean
    }
  | {
      directionId?: number | null
      enableCourseSelect: true
    }

type CreateGroupProps = DirectionSett & {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export interface FieldType {
  direction_id: number
  teacher_id: number | null
  name: string
}

type CreateGroupContentProps = CreateGroupProps & {}

const CreateGroupModalContent: FC<CreateGroupContentProps> = ({
  directionId,
  enableCourseSelect,
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const [form] = useForm()
  const [submitting, setSubmitting] = useState(false)

  const [directionsState, doFetchDirections] = useAsyncFn(directionAPI.getAll)
  const colleaguesState = useAsync(async () =>
    usersAPI.getColleaguesForGroup({ name: '' }),
  )
  const allColleagues = (colleaguesState.value?.data?.data ??
    []) as IColleague[]
  const allCourses = (directionsState.value?.data?.data ?? []) as ICourse[]

  useEffect(() => {
    if (enableCourseSelect) doFetchDirections()
  }, [])

  useEffect(() => {
    if (colleaguesState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка при получении списка педагогов',
        type: 'error',
      })
      onClose()
    }
  }, [colleaguesState.error])

  // forms

  const handlerSubmit: FormProps<FieldType>['onFinish'] = (values) => {
    setSubmitting(true)
    directionGroupApi
      .add({
        data: {
          direction_id: enableCourseSelect ? values.direction_id : directionId,
          teacher_id: values.teacher_id,
          name: values.name,
        },
      })
      .then((res) => {
        if (res?.data?.data?.id === undefined) throw new Error()

        onSuccess && onSuccess()
        addAlert({
          title: 'Успешно',
          text: 'Группа успешно создана',
          type: 'success',
        })
        onClose()
      })
      .catch((err) => {
        addAlert({
          title: 'Ошибка',
          text: 'Ошибка при создании группы',
          type: 'error',
        })
        onClose()
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <Form
      noValidate
      form={form}
      initialValues={{ teacher_id: null, name: '', direction_id: directionId }}
      onFinish={handlerSubmit}
      className={cn(mclasses.modalContainer, classes.modalContainer)}
    >
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>
            Создание новой группы
          </span>
        </div>
        <div className={mclasses.closeIcon} onClick={() => onClose()}>
          <IcoRemove />
        </div>
      </div>
      {colleaguesState.loading || directionsState.loading ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Preloader fullScreen="app" />
        </div>
      ) : (
        <div
          className={cn(
            mclasses.formWrapper,
            mclasses.overflowAuto,
            classes.formWrapper,
          )}
        >
          <div>
            {enableCourseSelect && (
              <>
                <p className={mclasses.label}>Курс</p>
                <FormItem
                  name="direction_id"
                  rules={[{ required: true, message: 'Выберите курс' }]}
                >
                  <Select
                    showSearch
                    className={cn(iclasses.newSelect, classes.selectField)}
                    popupClassName={iclasses.newSelectPopup}
                    placeholder="Выберите курс"
                    options={allCourses.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    loading={colleaguesState.loading}
                    filterOption={(input, option) =>
                      String(option?.label ?? '')
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </FormItem>
              </>
            )}
            <p className={mclasses.label}>Название группы</p>
            <FormItem
              name="name"
              rules={[{ required: true, message: 'Укажите название' }]}
              getValueProps={(value) => ({
                validate: form.getFieldError('name').length > 0,
              })}
            >
              {/*@ts-ignore*/}
              <InputNew
                className={iclasses.newInputDesign}
                placeholder={'Введите название'}
                errorText={' '}
                typeValid="custom"
              />
            </FormItem>
            <p className={mclasses.label}>Педагог</p>
            <FormItem
              name="teacher_id"
              rules={[{ required: true, message: 'Выберите педагога' }]}
            >
              <Select
                showSearch
                className={cn(iclasses.newSelect, classes.selectField)}
                popupClassName={iclasses.newSelectPopup}
                placeholder="Выберите педагога"
                options={allColleagues.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                loading={colleaguesState.loading}
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </FormItem>
          </div>
        </div>
      )}
      <div className={mclasses.actions}>
        <Button color="gray" onClick={() => onClose()}>
          Отменить
        </Button>
        <Button
          type="submit"
          color="primary"
          disabled={submitting || colleaguesState.loading || directionsState.loading}
          isLoading={submitting}
        >
          Подтвердить
        </Button>
      </div>
    </Form>
  )
}

const CreateGroupModal: FC<CreateGroupProps> = (props) => {
  const { open, onClose } = props

  return (
    <>
      <Modal
        active={open}
        className={mclasses.modalWrapper as any}
        setActive={() => onClose()}
      >
        <CreateGroupModalContent {...props} />
      </Modal>
    </>
  )
}

export default CreateGroupModal
